import { useTranslation } from 'react-i18next';
import { Box, Center, SimpleGrid, Spinner, Text, VisuallyHidden, VStack } from '@chakra-ui/react';
import {
  HiShieldCheck,
  HiAcademicCap,
  HiBell,
} from 'react-icons/hi2';
import { StatCard } from '@themes/aegix';
import { OnePageHeader as PageHeader } from '@/components/layout/OnePageHeader';
import { EventCard } from '@/components/EventCard';
import { useNotificationsDrawer } from '@/contexts/NotificationsDrawerContext';
import {
  useActiveEvents,
  useUnreadCount,
} from '@/hooks/useReunification';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function Dashboard() {
  const { t } = useTranslation();
  useDocumentTitle(t('nav.dashboard'));
  const { open: openNotifications } = useNotificationsDrawer();
  const { data: eventsData, isLoading: eventsLoading } = useActiveEvents();
  const { data: unreadData } = useUnreadCount();

  const activeEvents = eventsData?.results ?? [];
  const unreadCount = unreadData?.unread_count ?? 0;

  return (
    <Box>
      <PageHeader
        icon={HiShieldCheck}
        title={t('nav.dashboard')}
        subtitle={t('dashboard.subtitle')}
        showSearch={false}
        showNotifications={true}
        onNotificationClick={openNotifications}
        notificationCount={unreadCount}
        showColorMode={false}
      />

      <Box p={{ base: 4, md: 6 }}>
        {/* Stat cards */}
        <SimpleGrid columns={{ base: 2, md: 3 }} gap={4} mb={6}>
          <StatCard
            label={t('dashboard.activeEvents')}
            value={activeEvents.length}
            icon={HiShieldCheck}
            colorPalette="red"
          />
          <StatCard
            label={t('dashboard.unreadAlerts')}
            value={unreadCount}
            icon={HiBell}
            colorPalette="orange"
          />
          <StatCard
            label={t('dashboard.totalStudents')}
            value={
              activeEvents.reduce((sum, e) => sum + (e.stats?.total_students ?? 0), 0)
            }
            icon={HiAcademicCap}
            colorPalette="blue"
          />
        </SimpleGrid>

        {/* Active events */}
        {eventsLoading ? (
          <Center py={12} aria-live="polite" role="status">
            <Spinner size="lg" />
            <VisuallyHidden>{t('dashboard.loading')}</VisuallyHidden>
          </Center>
        ) : activeEvents.length > 0 ? (
          <Box>
            <Text fontSize="md" fontWeight="600" color="text.primary" mb={3}>
              {t('dashboard.activeEventsSection')}
            </Text>
            <VStack gap={3} align="stretch">
              {activeEvents.map((event) => (
                <EventCard key={event.public_id} event={event} />
              ))}
            </VStack>
          </Box>
        ) : (
          <Center py={12}>
            <Text color="text.secondary">{t('dashboard.noActiveEvents')}</Text>
          </Center>
        )}
      </Box>
    </Box>
  );
}
